/**
 * Wire types — same field names as the backend's drizzle-zod schemas in
 * `@transpay/shared-schema`. We re-declare them here to keep the frontend
 * bundle free of server-side dependencies (drizzle-orm, pg).
 *
 * IMPORTANT: any field name change here must also be applied in the backend
 * schema. The two should remain in sync; the SETUP.md "continuity of names"
 * section is the contract.
 */

export type Scope = 'internal' | 'external';
export type TrxStatus = 'pending' | 'paid' | 'failed';

export type Profile = {
  id: number;
  email: string;
  accountName: string;
  userType: Scope;
  /** UUID string for merchant scope; null for internal/superuser. */
  merchantId: string | null;
  isSuperuser: boolean;
};

/**
 * Returned by GET /api/auth/me. Adds the resolved role + permission set so
 * the frontend can gate menus and routes without an extra round-trip.
 *
 * `permissions === null` is the superuser sentinel — render everything.
 */
export type Me = Profile & {
  role: { id: number; slug: string; name: string; scope: 'internal' | 'merchant' } | null;
  merchant: { id: string; name: string } | null;
  permissions: string[] | null;
};

export type LoginResponse = {
  token: string;
  profile: Profile;
};

export type ApiError = {
  code: string;
  message: string;
  detail?: unknown;
};

// MUST mirror `@transpay/shared-payok`'s PayokPaymentMethod exactly — the
// backend validates `payment_methods.enabled` (and acquirer/merchant configs)
// against this enum, and the casing is sent verbatim to PAYOK, so codes like
// `MandiriVA` / `PermataVA` are camelCase while `BNIVA` / `MUAMALATVA` are
// upper. A mismatch here makes the UI offer codes the API rejects.
export type PayokPaymentMethod =
  | 'QRIS'
  | 'StaticQRIS'
  | 'BNIVA'
  | 'BCAVA'
  | 'BRIVA'
  | 'BSIVA'
  | 'CIMBVA'
  | 'MandiriVA'
  | 'PermataVA'
  | 'DanamonVA'
  | 'BNCVA'
  | 'INAVA'
  | 'BNISyariahVA'
  | 'ALTOVA'
  | 'SinarmasVA'
  | 'PrimaVA'
  | 'MUAMALATVA'
  | 'NobuVA'
  | 'DANAWALLET'
  | 'OVOWALLET'
  | 'SHOPEEPAY-APP'
  | 'SHOPEEPAY-WEB'
  | 'LINKAJA-APP'
  | 'LINKAJA-WEB'
  | 'ConvenienceStore';

export const PAYOK_PAYMENT_METHODS: readonly PayokPaymentMethod[] = [
  'QRIS',
  'StaticQRIS',
  'BNIVA',
  'BCAVA',
  'BRIVA',
  'BSIVA',
  'CIMBVA',
  'MandiriVA',
  'PermataVA',
  'DanamonVA',
  'BNCVA',
  'INAVA',
  'BNISyariahVA',
  'ALTOVA',
  'SinarmasVA',
  'PrimaVA',
  'MUAMALATVA',
  'NobuVA',
  'DANAWALLET',
  'OVOWALLET',
  'SHOPEEPAY-APP',
  'SHOPEEPAY-WEB',
  'LINKAJA-APP',
  'LINKAJA-WEB',
  'ConvenienceStore',
];

export type PayokConfig = {
  enabled: boolean;
  merchantId: string | null;
  paymentMethodCode: PayokPaymentMethod | string | null;
  countryCode: string | null;
  currency: string | null;
  language: string | null;
  baseUrl: string | null;
  orderCreatePath: string | null;
  orderQueryPath: string | null;
  disbursementCreatePath: string | null;
  depositNotificationUrl: string | null;
  /** True when the displayed URL came from the env override, not the DB column. */
  depositNotificationUrlIsEnvOverride?: boolean;
  /** Original DB column value; useful for diagnosing env-override vs stored value. */
  depositNotificationUrlStored?: string | null;
  disbursementNotificationUrl: string | null;
  disbursementNotificationUrlIsEnvOverride?: boolean;
  disbursementNotificationUrlStored?: string | null;
  returnUrl: string | null;
  /** TransPay-generated public key — share with PAYOK ops. */
  transpayPublicKey: string | null;
  /** PAYOK's platform public key (PEM, post-normalize). */
  payokPublicKey: string | null;
};

export type MerchantDto = {
  /** Merchant primary key — UUID v4 generated server-side at create. */
  id: string;
  name: string;
  email: string;
  depositFeeBps: number;
  disbursementFeeIdr: number;
  /** Per-VA-code cash-in fee = percentage (bps, 0-10000) + flat (whole IDR), both
   *  applied: fee = round(amount*bps/10000) + fixed. null/absent code → percentage
   *  fee (depositFeeBps) only. */
  vaFees: Record<string, { bps: number; fixed: number }> | null;
  /** Per-account-type cash-out fee = percentage (bps, 0-10000) + flat (whole IDR),
   *  keyed BANK/WALLET/BPR, both applied on the amount. null or absent type →
   *  falls back to the flat disbursementFeeIdr default. */
  disbursementFees: Record<string, { bps: number; fixed: number }> | null;
  /** Per-transaction limits in IDR (whole rupiah). Migration 0011. */
  cashinMinAmount: number;
  cashinMaxAmount: number;
  cashoutMinAmount: number;
  cashoutMaxAmount: number;
  apiAccessEnabled: boolean;
  /** Per-merchant gate for server-to-server disbursement (money-out via API key). */
  apiDisbursementEnabled: boolean;
  suspendedAt: string | null;
  transpayClientId: string;
  transpayClientTokenSuffix: string | null;
  depositWebhookUrl: string | null;
  disbursementWebhookUrl: string | null;
  payok: PayokConfig | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * A merchant row as returned by the list endpoint (`GET /api/merchants`), which
 * augments the base merchant with per-merchant money columns:
 *  - activeBalance / pendingBalance: denormalized merchant_wallet balances.
 *  - totalCashIn / totalCashOut: GROSS amount of *paid* deposits / disbursements
 *    (pending + failed excluded). All in whole IDR.
 */
export type MerchantListItem = MerchantDto & {
  activeBalance: number;
  pendingBalance: number;
  totalCashIn: number;
  totalCashOut: number;
};

export type MerchantCredentialsReveal = {
  data: MerchantDto;
  credentials: {
    transpayClientId: string;
    transpayClientToken: string;
    warning: string;
  };
  webhookSecret?: string;
};

export type WalletDto = {
  merchantId: string;
  pendingBalance: number;
  activeBalance: number;
  reservedBalance: number;
  /** Carry-over debt: deferred deduction paid down from future settlements. */
  outstandingBalance: number;
  lifetimeDeposit: number;
  lifetimeDisbursement: number;
  lifetimeFee: number;
  updatedAt: string;
};

/**
 * Platform-wide wallet totals across ALL merchants. Powers the internal-user
 * wallet page (each field is the SUM over every merchant wallet).
 */
export type InternalWalletSummary = {
  pendingBalance: number;
  activeBalance: number;
  reservedBalance: number;
  outstandingBalance: number;
  lifetimeDeposit: number;
  lifetimeDisbursement: number;
  lifetimeFee: number;
  /** How many merchant wallets were summed. */
  merchantCount: number;
  updatedAt: string | null;
};

/** A cross-merchant ledger row — like WalletLedgerRow plus the merchant name. */
export type InternalWalletLedgerRow = WalletLedgerRow & {
  merchantName: string;
};

/** UI mutation category used by the internal-wallet Type filter. */
export type WalletMutationCategory = 'settlement' | 'withdrawal' | 'adjustment' | 'fee';

/** Filter for the internal cross-merchant ledger. Arrays = multi-select. */
export type InternalWalletLedgerFilter = {
  trxId?: string;
  merchantId?: string[];
  type?: WalletMutationCategory[];
  createdBy?: string[];
  cursor?: string;
  limit?: number;
};

/** Option lists for the internal-wallet filter dropdowns. */
export type WalletLedgerFilterOptions = {
  merchants: { id: string; name: string }[];
  createdBy: string[];
};

export type WalletLedgerRow = {
  id: number;
  merchantId: string;
  trxId: string | null;
  trxType: string;
  amountPending: number;
  amountActive: number;
  amountReserved: number;
  amountOutstanding: number;
  balanceAfterPending: number;
  balanceAfterActive: number;
  balanceAfterReserved: number;
  balanceAfterOutstanding: number | null;
  reason: string | null;
  createdAt: string;
  createdBy: string;
};

export type DepositRow = {
  trxId: string;
  referenceId: string;
  /**
   * Free-form text the deposit-svc stamps at creation time. Currently the
   * merchant's display name (deposits come from merchant API keys, not user
   * sessions). Kept for backward compat; the table prefers `merchantName`.
   */
  createdBy: string;
  /** Source IP of the cashin request hitting TransPay's API. */
  fromIp: string;
  merchantId: string;
  merchantName: string;
  merchantEmail: string;
  method: string;
  amount: number;
  fee: number;
  merchantRevenue: number;
  trxStatus: TrxStatus;
  paidAt: string | null;
  /** Scheduled settlement timestamp (see computeSettleAt: same day 19:00 / next day 13:00 GMT+7 by default). */
  settleAt: string | null;
  /** When the settlement worker actually moved pending → active. */
  settledAt: string | null;
  createdAt: string;
};

export type DepositDetail = {
  transpay: {
    trxId: string;
    merchantId: string;
    merchantName: string;
    referenceId: string;
    createdBy: string;
    method: string;
    fromIp: string;
    trxStatus: TrxStatus;
    paidAt: string | null;
    payersName: string | null;
    rrnNumber: string | null;
    amount: number;
    fee: number;
    merchantRevenue: number;
    remark: string;
    createdAt: string;
    settleAt: string | null;
    settledAt: string | null;
  };
  payok?: {
    merchantOrderId: string;
    platformOrderId: string | null;
    createRequest: unknown;
    createResponse: unknown;
    callbackPayload: unknown;
    callbackReceivedAt: string | null;
  } | null;
};

/**
 * Response of POST /api/deposits/:trxId/status and
 * POST /api/disbursements/:trxId/status (manual status override, internal).
 */
export type ManualStatusUpdateResponse = {
  ok: true;
  trxId: string;
  previousStatus: TrxStatus;
  status: TrxStatus;
  paidAt: string | null;
  settleAt: string | null;
  callbackRequested: boolean;
  /** true when a new webhook_outbox row was queued for the merchant. */
  callbackQueued: boolean;
  callbackSkippedReason: 'no_event_for_pending' | 'webhook_url_not_configured' | null;
  outboxId: string | null;
  targetUrl: string | null;
};

export type IpWhitelistRow = {
  id: number;
  cidr: string;
  label: string;
  enabled: boolean;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt: string | null;
};

export type CursorPage<T> = {
  rows: T[];
  nextCursor: string | null;
  hasMore: boolean;
  /** Total rows matching the filter (OFFSET / numbered pagination). */
  total: number;
  /** 1-based page when the request used `page`; null in cursor mode. */
  page: number | null;
  pageCount: number;
};

/** One aggregate bucket of a filtered list summary. */
export type ListSummaryBucket = {
  count: number;
  amount: number;
  fee: number;
  /** Deposits: net revenue. Disbursements: signed wallet impact (negative). */
  merchantRevenue: number;
};

/**
 * GET /api/deposits/summary and /api/disbursements/summary — aggregates over
 * the same filter set as the list. `byMethod` is grouped by payment method
 * for deposits (`dimension: 'method'`) and by destination bank for
 * disbursements (`dimension: 'bank'`).
 */
export type ListSummary = {
  dimension: 'method' | 'bank';
  totals: ListSummaryBucket;
  byStatus: Array<ListSummaryBucket & { status: TrxStatus }>;
  byMethod: Array<ListSummaryBucket & { key: string }>;
};
